/**
 * PATH: packages/ui/src/kanban/utils/boardAccess.ts
 * FILE_ID: UI-KANBAN-ACCESS-UTIL-001
 * PURPOSE: Pure constants + guard helpers for the board "Access" surface
 *          (member roles + visibility band), shared by the Access popover
 *          and the board context menu so labels/options live in one place.
 * WHY: Operators manage "who has access" to a board — which people, at
 *      which permission (role), plus the org-wide visibility band. Keeping
 *      these as pure I→O data prevents drift between the popover and the
 *      menu and lets us unit-test the guard rails (e.g. last-owner).
 * FLOW:
 *   ┌──────────────┐   ┌───────────────┐   ┌──────────────────────┐
 *   │ memberships  │ → │ countOwners / │ → │ disable demote/remove │
 *   │ + role pick  │   │ isOnlyOwner   │   │ of the only Owner     │
 *   └──────────────┘   └───────────────┘   └──────────────────────┘
 * DEPENDENCIES:
 *   - ../types: BoardMembership, BoardVisibility (shape of the inputs)
 * RELATED:
 *   - ./../components/KanbanBoardMembersPopover.tsx: the Access popover
 *   - ./../components/BoardVisibilitySelector.tsx: visibility segmented control
 *   - ./../components/BoardMemberRow.tsx: per-member role row
 */
import type { Board, BoardMembership, BoardVisibility } from '../types'

export type BoardRole = BoardMembership['role']

/** Roles in descending-privilege order — drives the role <select>. */
export const BOARD_ROLES: BoardRole[] = ['OWNER', 'ADMIN', 'EDITOR', 'VIEWER']

export const BOARD_ROLE_LABELS: Record<BoardRole, string> = {
  OWNER: 'Owner',
  ADMIN: 'Admin',
  EDITOR: 'Editor',
  VIEWER: 'Viewer',
}

/** One-line description of what each role can do — surfaced as a hint. */
export const BOARD_ROLE_HINTS: Record<BoardRole, string> = {
  OWNER: 'Full control, incl. managing access and deleting the board.',
  ADMIN: 'Manage members, lanes and board settings.',
  EDITOR: 'Add, edit and move cards.',
  VIEWER: 'Read-only — can see the board but not change it.',
}

export interface VisibilityOption {
  value: BoardVisibility
  label: string
  hint: string
}

/** Compact labels for the in-popover segmented selector.
 *
 * The hints deliberately separate the two questions operators kept
 * conflating (Issue B, 2026-07-01): "who can SEE this board" (the
 * visibility band) vs "what can each person DO" (their role in the list
 * below). PRIVATE / MEMBERS gate visibility by the member list; ORG makes
 * the board visible to the whole org, so the member list there only sets
 * ELEVATED or RESTRICTED roles — it does not grant/remove basic access.
 */
export const BOARD_VISIBILITY_OPTIONS: VisibilityOption[] = [
  {
    value: 'PRIVATE',
    label: 'Private',
    hint: 'Only you can see this board. Adding anyone switches it to “Members”.',
  },
  {
    value: 'BOARD_MEMBERS',
    label: 'Members',
    hint: 'Only the people in the list below can see this board.',
  },
  {
    value: 'ORG',
    label: 'Organisation',
    // Issue B follow-up (2026-07-01): ONE clear line under the selector.
    // The old hint duplicated the "add someone below to set a role"
    // sentence that also appeared in the status line, empty state AND the
    // add-person helper. That elaboration now lives ONCE under the input
    // (see ``accessInviteCopy`` help), so this stays a single fact.
    hint: 'Everyone in the org can see this board.',
  },
]

export const BOARD_VISIBILITY_HINTS: Record<BoardVisibility, string> = {
  PRIVATE: BOARD_VISIBILITY_OPTIONS[0].hint,
  BOARD_MEMBERS: BOARD_VISIBILITY_OPTIONS[1].hint,
  ORG: BOARD_VISIBILITY_OPTIONS[2].hint,
}

/** Copy for the "add person" control, contextual to the visibility band.
 *
 * Issue B (2026-07-01): the popover showed an "Invite someone…" affordance
 * identically for every band, which reads as "grant access". On an ORG
 * board everyone already has access, so adding a person there only sets a
 * ROLE — calling it "invite" is misleading. This pure helper keeps the
 * three strings (section label, input placeholder, and an optional
 * clarifying help line) in one testable place so the popover and any other
 * caller don't drift.
 */
export interface AccessInviteCopy {
  /** Section label above the role picker + add control. */
  addLabel: string
  /** Placeholder for the person-picker input. */
  placeholder: string
  /** Optional one-line clarification (ORG only); '' when not needed. */
  help: string
}

export function accessInviteCopy(visibility: BoardVisibility): AccessInviteCopy {
  if (visibility === 'ORG') {
    return {
      addLabel: 'Give a role',
      placeholder: 'Give someone a specific role…',
      // The ONE helper under the input (BUG 3, 2026-07-01). On an ORG board
      // everyone can already see it, so the meaningful action is ELEVATING
      // access — Viewer is a no-op here (it only bites on Private/Members).
      help: 'Adding someone here gives them a higher role (Editor/Admin). Viewer only matters on Private/Members boards.',
    }
  }
  return {
    addLabel: 'Invite as',
    placeholder: 'Invite someone…',
    help: '',
  }
}

/**
 * Default role to pre-select in the "add person" dropdown, by visibility.
 *
 * BUG 3 (2026-07-01): on an ORG board everyone can already SEE it, so
 * granting "Viewer" is a no-op — the meaningful action is ELEVATING access
 * (Editor / Admin). So ORG defaults to EDITOR. PRIVATE / MEMBERS keep the
 * existing EDITOR default (the roster gates visibility there, and Editor is
 * the sensible "can collaborate" starting point). Pure I→O.
 */
const DEFAULT_INVITE_ROLE_BY_VISIBILITY: Record<BoardVisibility, BoardRole> = {
  // ORG: everyone already sees it → default to the elevated EDITOR (Viewer is
  // a no-op). PRIVATE / BOARD_MEMBERS: keep the historical EDITOR default so
  // an invited teammate can collaborate; dial down to Viewer explicitly.
  ORG: 'EDITOR',
  BOARD_MEMBERS: 'EDITOR',
  PRIVATE: 'EDITOR',
}

export function defaultInviteRole(visibility: BoardVisibility): BoardRole {
  return DEFAULT_INVITE_ROLE_BY_VISIBILITY[visibility] ?? 'EDITOR'
}

/** The owner contact rendered on the always-visible "Owner" line. */
export interface BoardOwnerDisplay {
  id: number | null
  /** Resolved display name, or null when only an id is known (the caller
   *  then looks it up in the org-member list / shows "User #id"). */
  name: string | null
  avatar: string | null
  /** 'Owner' normally; 'Created by' when the backend flagged a created_by
   *  fallback (the board's ``owner`` FK was null, e.g. a seeded JOB board). */
  label: 'Owner' | 'Created by'
}

/**
 * Resolve the owner to ALWAYS show at the top of the Access popover
 * (BUG 2, 2026-07-01). Prefers the backend ``owner_display`` object (name +
 * avatar + created_by fallback); when absent (older BE bundle) it falls back
 * to ``resolveBoardOwnerId`` so the caller can still look the name up in the
 * org-member list. Pure I→O — never throws.
 */
export function resolveBoardOwnerDisplay(
  board: Pick<Board, 'owner' | 'created_by' | 'owner_display'>,
  members: readonly BoardMembership[],
): BoardOwnerDisplay {
  const od = board.owner_display
  if (od && (od.id != null || (od.name ?? '').trim())) {
    return {
      id: od.id ?? null,
      name: (od.name ?? '').trim() || null,
      avatar: od.avatar ?? null,
      label: od.is_creator_fallback ? 'Created by' : 'Owner',
    }
  }
  return { id: resolveBoardOwnerId(board, members), name: null, avatar: null, label: 'Owner' }
}

/** Count active (non-revoked) OWNER memberships. */
export function countActiveOwners(members: readonly BoardMembership[]): number {
  return members.filter((m) => m.role === 'OWNER' && !m.revoked_at).length
}

/**
 * FEAT-2026-07-01-KANBAN-BOARD-ADMIN-GATE — pure predicate: may the
 * current user ADMINISTER this board? Administration = changing the
 * visibility band and managing membership (invite / re-role / remove).
 * That is a strictly higher bar than "write a card": only the board
 * owner/creator or an OWNER/ADMIN board role qualifies.
 *
 * f(board, members, currentUserId) -> boolean. Mirrors the backend
 * ``user_can_administer_board`` so the popover hides exactly the controls
 * the server would 403.
 *
 * Fail-OPEN policy for the two cases the client genuinely cannot judge —
 * the BACKEND is the authoritative gate and 403s a true non-admin:
 *   * ``currentUserId`` unknown → return true (legacy hosts that don't
 *     thread a user id keep today's behaviour).
 *   * user has NO membership row and is not owner/creator → ambiguous
 *     (could be an org admin viewing an ORG board they never joined) →
 *     return true.
 * We only DEMOTE to read-only when we can POSITIVELY see the caller is a
 * non-admin board member (an active EDITOR/VIEWER row) — the reported bug
 * (a VIEWER with a membership row seeing full admin controls).
 */
export function canAdministerBoard(
  board: Pick<Board, 'owner' | 'created_by'>,
  members: readonly BoardMembership[],
  currentUserId: number | null | undefined,
): boolean {
  if (currentUserId == null) return true
  if (board.owner === currentUserId || board.created_by === currentUserId) {
    return true
  }
  const mine = members.filter(
    (m) => !m.revoked_at && m.user === currentUserId,
  )
  if (mine.some((m) => m.role === 'OWNER' || m.role === 'ADMIN')) return true
  // A KNOWN board member holding only EDITOR/VIEWER is NOT an administrator.
  if (mine.length > 0) return false
  return true
}

/**
 * Resolve the user id of the board's OWNER so a read-only viewer knows
 * who to contact. Prefers an active OWNER membership row (the roster's
 * source of truth), then the ``owner`` FK, then ``created_by``. Returns
 * null when none can be determined. Pure I→O.
 */
export function resolveBoardOwnerId(
  board: Pick<Board, 'owner' | 'created_by'>,
  members: readonly BoardMembership[],
): number | null {
  const ownerRow = members.find((m) => m.role === 'OWNER' && !m.revoked_at)
  if (ownerRow) return ownerRow.user
  if (board.owner != null) return board.owner
  if (board.created_by != null) return board.created_by
  return null
}

/**
 * True when `membership` is the ONLY active Owner of the board. Used to
 * disable demoting / removing it client-side — a disabled control reads
 * better than a round-trip `last_owner` 400 from the backend (which still
 * enforces this server-side as the source of truth).
 *
 * Returns false for any non-Owner row (they are always editable).
 */
export function isOnlyActiveOwner(
  members: readonly BoardMembership[],
  membership: Pick<BoardMembership, 'role'>,
): boolean {
  if (membership.role !== 'OWNER') return false
  return countActiveOwners(members) <= 1
}
